/**
 * PROCESS FIELDS INTO SPECIFIC TYPES
 *
 * Flatfile returns everything as a string,
 * so we have to pre-process each field into
 * the values we expect.
 */

import { DateTime, DurationLike } from 'luxon';
import * as z from 'zod';

import { convertStringToDate } from '../utils';
import { FOREIGN_COUNTRY_CODE_MAP } from './CountryCodes';
import { STATE_ABBREVIATION_TO_FULL_NAME } from './StateAbbreviations';
import { formatEIN, formatSSN, isEINValid, isSSNValid } from './taxIdentificationNumbers';

const ZIPCODE_REGEX = '^[0-9]{5}(?:-[0-9]{4})?$';

const URL_REGEX =
  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
export const isValidUrl = (url: string): boolean => URL_REGEX.test(url);

const GIVEN_NAME_ALLOWED_CHARS_REGEX = /^((?!,).)*$/;

// --- STRINGS --- //

export const PersonNameString = z.string().refine(
  (val?: string) => {
    if (val == null) {
      return true;
    }
    const regex = new RegExp(GIVEN_NAME_ALLOWED_CHARS_REGEX);
    return regex.test(val);
  },
  {
    message: 'Invalid name provided (commas not allowed).',
  },
);

// --- DATE STRINGS --- //

const dateTimeFromString = (val: string) => DateTime.fromJSDate(convertStringToDate(val)).toUTC();

export const DateString = z
  .string()
  .refine(
    (val?: string) => {
      if (val == null) return false;

      let parsedDate = DateTime.fromISO(val);
      if (parsedDate.isValid) return true;

      const allowedFormats = [
        'M/d/yyyy',
        'M/d/yy',
        'MM/dd/yy',
        'MM/dd/yyyy',
        'MMM d, yyyy',
        'MMM dd, yyyy',
        'MMMM d, yyyy',
        'MMMM dd, yyyy',
        'yyyy-MM-dd',
        'yyyy-MM-d',
        'yyyy-M-dd',
        'yyyy-M-d',
      ];

      for (const format of allowedFormats) {
        parsedDate = DateTime.fromFormat(val, format);
        if (parsedDate.isValid) return true;
      }

      return false;
    },
    { message: 'Invalid date format' },
  )
  .meta({
    id: 'plainDateStringInput',
    type: 'string',
    effectType: 'input',
    description:
      'A plain date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 string in UTC time. If a timestamp is included, it will be removed and the date will be interpreted as 00:00:00 UTC.',
  });

// DateString modifiers to be used with `.and()`. ie, DateString.and(isValidPaymentDate)

export const dateIsOnFirstOfMonth = z
  .unknown()
  .refine(
    (val?: unknown) => {
      if (val == null || typeof val !== 'string') return true;
      const parsed = DateString.safeParse(val);
      if (val && parsed.success) {
        const date = dateTimeFromString(parsed.data);
        return date.day === 1;
      }
      return true;
    },
    {
      message: 'Date must be on the first of the month.',
    },
  )
  .meta({
    id: 'plainDateStringInputFirstOfMonth',
    type: 'string',
    effectType: 'input',
    description:
      'A date string representing the 1st of a month. A plain date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 string in UTC time. If a timestamp is included, it will be removed and the date will be interpreted as 00:00:00 UTC. ',
  });

export const isValidPaymentDate = z
  .unknown()
  .refine(
    (val?: unknown) => {
      if (val == null || typeof val !== 'string') return true;
      const parsed = DateString.safeParse(val);
      if (val && parsed.success) {
        const date = dateTimeFromString(parsed.data);
        return date.day === 1 || date.day === 25;
      }
      return true;
    },
    {
      message: 'Date must be on the first of the month. For HELOC or construction loans the 25th is also valid.',
    },
  )
  .meta({
    id: 'plainDateStringInputPaymentDate',
    type: 'string',
    effectType: 'input',
    description:
      'A date string representing the 1st or 25th day of a month. A plain date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 string in UTC time. If a timestamp is included, it will be removed and the date will be interpreted as 00:00:00 UTC. ',
  });

export const dateIsWithinRange = (range: DurationLike) =>
  z.unknown().refine(
    (val?: unknown) => {
      if (val == null || typeof val !== 'string') return true;
      const parsed = DateString.safeParse(val);
      if (val && parsed.success) {
        const dateTime = dateTimeFromString(parsed.data);
        return dateTime >= DateTime.now().minus(range) && dateTime <= DateTime.now().plus(range);
      }
      return true;
    },
    {
      message: 'Date is out of acceptable range.',
    },
  );

export const dateIsTodayOrInThePast = z
  .unknown()
  .refine(
    (val?: unknown) => {
      if (val == null || typeof val !== 'string') return true;
      const parsed = DateString.safeParse(val);
      if (val && parsed.success) {
        const dateTime = dateTimeFromString(parsed.data);
        return dateTime <= DateTime.now();
      }
      return true;
    },
    {
      message: 'Date must be today or in the past.',
    },
  )
  .meta({
    id: 'plainDateStringInputBeforeCurrentDate',
    type: 'string',
    effectType: 'input',
    description:
      'A date string representing the current date or a date in the past. A plain date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 string in UTC time. If a timestamp is included, it will be removed and the date will be interpreted as 00:00:00 UTC. ',
  });

export const MAX_FUTURE_FUNDING_DAYS = 3;

export const dateIsAtMostThreeDaysInFuture = z
  .unknown()
  .refine(
    (val?: unknown) => {
      if (val == null || typeof val !== 'string') return true;
      const parsed = DateString.safeParse(val);
      if (val && parsed.success) {
        const dateTime = dateTimeFromString(parsed.data);
        return dateTime <= DateTime.now().plus({ days: MAX_FUTURE_FUNDING_DAYS }).endOf('day');
      }
      return true;
    },
    {
      message: 'Date must be no more than 3 days in the future.',
    },
  )
  .meta({
    id: 'plainDateStringInputAtMostThreeDaysInFuture',
    type: 'string',
    effectType: 'input',
    description:
      'A date string representing a date no more than three days after the current date. A plain date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 string in UTC time. If a timestamp is included, it will be removed and the date will be interpreted as 00:00:00 UTC. ',
  });

// --- NUMBERS --- //
export const Float = z.preprocess((val: any) => {
  if (typeof val === 'string') {
    const number = Number(val);
    return Number.isNaN(number) ? val : number;
  }
  return val;
}, z.number());

export const NonNegativeFloat = Float.refine(
  (val?: number) => {
    if (val == null) return true;
    return val >= 0;
  },
  {
    message: 'Value must be a non-negative number.',
  },
);

export const PositiveFloat = Float.refine(
  (val?: number) => {
    if (val == null) return true;
    return val > 0;
  },
  {
    message: 'Value must be greater than zero.',
  },
);

export const NonNegativeInteger = Float.refine(
  (val?: number) => {
    if (val == null) return true;
    const regex = new RegExp('^[0-9]*$');
    return regex.test(`${val}`);
  },
  {
    message: 'Value must be a non-negative integer (no decimals).',
  },
);

export const PositiveInteger = NonNegativeInteger.refine((val) => val > 0, {
  message: 'Number must be greater than 0',
});

export const InterestRate = Float.refine((val?: number) => {
  if (val == null) return true;
  const regex = new RegExp('^(0+.?[0-9]*|.[0-9]+)$');
  return regex.test(`${val}`);
});

export const MonetaryValue = z
  .union([
    z
      .string()
      .trim()
      .transform((val) => val.replace(/[,$]/g, '')),
    z.number(),
  ])
  .pipe(z.coerce.number<string>().multipleOf(0.01));

export const NonNegativeMonetaryValue = z
  .union([
    z
      .string()
      .trim()
      .transform((val) => val.replace(/[,$]/g, '')),
    z.number(),
  ])
  .pipe(z.coerce.number<string>().nonnegative().multipleOf(0.01));

export const PositiveMonetaryValue = z
  .union([
    z
      .string()
      .trim()
      .transform((val) => val.replace(/[,$]/g, '')),
    z.number(),
  ])
  .pipe(z.coerce.number<string>().positive().multipleOf(0.01));

export const NonNegativePercentage = z.preprocess((val) => {
  if (typeof val === 'string' && val.includes('%')) {
    return parseFloat(val.replace('%', '').trim());
  }
  return val;
}, NonNegativeFloat);

export const PositivePercentage = z.preprocess(
  (val) => {
    if (typeof val === 'string' && val.includes('%')) {
      return parseFloat(val.replace('%', '').trim());
    }
    return val;
  },
  Float.refine(
    (val?: number) => {
      if (val == null) return true;
      return val > 0;
    },
    {
      message: 'Percentage must be greater than zero.',
    },
  ),
);

// --- BOOLEANS --- //

// This follows same rules as Flatfile's boolean parser.
// See Flatfile's boolean parser rules here: https://flatfile.com/docs/2.0/javascript/fields/#type
export const Checkbox = z.preprocess((val: any) => {
  if (typeof val === 'string') {
    val = val.toLowerCase();
    if (['true', 'false'].includes(val)) {
      return val === 'true';
    }
    if (['1', '0'].includes(val)) {
      return val === '1';
    }
    if (['yes', 'no'].includes(val)) {
      return val === 'yes';
    }
    if (['y', 'n'].includes(val)) {
      return val === 'y';
    }
    if (['on', 'off'].includes(val)) {
      return val === 'on';
    }
    if (['enabled', 'disabled'].includes(val)) {
      return val === 'enabled';
    }
  }
  return val;
}, z.boolean());

// --- SPECIALIZED --- //
const InvalidSSNMessage = 'Invalid social security number provided.';

// Spreadsheet tools parse numeric-looking SSN cells as numbers, dropping leading zeros.
// Only 7- and 8-digit values are padded (at most two restored zeros): an SSN's area
// number cannot be 000, so anything shorter is a genuine mistake, not dropped zeros.
export const coerceSSN = (val: unknown): unknown => {
  if (val == null || val === '') return val;
  const str = typeof val === 'number' ? String(val) : val;
  if (typeof str === 'string' && /^\d{7,8}$/.test(str)) {
    return str.padStart(9, '0');
  }
  return str;
};

export const SocialSecurityNumber = z
  .preprocess(coerceSSN, z.string())
  .refine(
    (val?: string) => {
      if (val == null) return true;
      return isSSNValid(val);
    },
    {
      message: InvalidSSNMessage,
    },
  )
  .transform(formatSSN);

// `.optional()` stays on the outside so the key remains optional in object input types
// (a bare preprocess would make the field required with type `unknown`).
export const OptionalSocialSecurityNumber = z
  .preprocess(coerceSSN, z.string())
  .refine(
    (val: string) => {
      if (val.length === 0) return true;
      return isSSNValid(val);
    },
    {
      message: InvalidSSNMessage,
    },
  )
  .optional();

export const EmployerIdentificationNumber = z
  .string()
  .refine(
    (val?: string) => {
      if (val == null) return true;
      return isEINValid(val);
    },
    {
      message: 'Invalid EIN provided.',
    },
  )
  .transform(formatEIN);

export const AddressLine = z.string().refine((val) => !/@/.test(val), { message: 'Invalid character "@" found.' });

export const StateAbbreviation = z
  .string()
  .length(2, 'Value must be a two letter state abbreviation.')
  .toUpperCase()
  .refine(
    (value) => Object.keys(STATE_ABBREVIATION_TO_FULL_NAME).includes(value),
    'Value must be a two letter state abbreviation.',
  )
  .meta({
    id: 'stateAbbreviation',
    type: 'string',
    description: 'The region of the servicer payment address',
    example: 'CA',
    enum: Object.keys(STATE_ABBREVIATION_TO_FULL_NAME),
  });
export type StateAbbreviation = z.infer<typeof StateAbbreviation>;

export const coerceZipcode = (val: unknown): unknown => {
  if (val == null || val === '') return val;
  const str = typeof val === 'number' ? String(val) : val;
  if (typeof str === 'string' && /^\d{1,4}$/.test(str)) {
    return str.padStart(5, '0');
  }
  return str;
};

export const Zipcode = z
  .preprocess(coerceZipcode, z.string())
  .refine(
    (val?: string) => {
      if (val == null) return true;
      const regex = new RegExp(ZIPCODE_REGEX);
      return regex.test(val);
    },
    {
      message: 'Invalid zipcode provided.',
    },
  )
  .describe('The postal code of the servicer payment address. Can be either 5 or 9 digits.');

export const ForeignCountryCode = z
  .string()
  .trim()
  .toUpperCase()
  .refine((val) => Object.keys(FOREIGN_COUNTRY_CODE_MAP).includes(val), {
    message: 'Invalid country code provided (see ISO 3166-1 alpha-2)',
  })
  .describe('ISO 3166-1 alpha-2 country code.');
export type ForeignCountryCode = z.infer<typeof ForeignCountryCode>;

export const CountryCode = z.union([ForeignCountryCode, z.literal('USA'), z.literal('US')]);
export type CountryCode = z.infer<typeof CountryCode>;
